"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { ROOT_AUTH_URL, ROOT_PRIORITY_URL } from "@/constants/url";
import { cn } from "@/lib/utils";

const navLinks = [
  { href: "#features", label: "Возможности" },
  { href: "#pricing", label: "Тарифы" },
  { href: "#faq", label: "FAQ" },
];

const AUTH_HREF = `${ROOT_AUTH_URL}/login?redirect_path=${ROOT_PRIORITY_URL}&no_return=0`;

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (mobileOpen) {
      const close = () => setMobileOpen(false);
      window.addEventListener("scroll", close, { passive: true });
      return () => window.removeEventListener("scroll", close);
    }
  }, [mobileOpen]);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 flex justify-center px-4">
        <motion.div
          layout
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className={cn(
            "flex items-center justify-between gap-6",
            scrolled
              ? "mt-3 max-w-[560px] w-full rounded-full border border-white/[0.08] bg-black/60 backdrop-blur-xl px-4 py-2"
              : "mt-0 max-w-7xl w-full bg-transparent px-4 sm:px-6 lg:px-8 py-4"
          )}
        >
          {/* Логотип */}
          <Link href="/" className="flex items-center gap-2 shrink-0">
            <img src="/favicon.png" alt="MPUNIT" className="w-7 h-7" />
            <span className="text-white font-semibold text-base">MPUNIT</span>
          </Link>

          {/* Навигация (десктоп) */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-sm text-white/50 hover:text-white transition-colors duration-200"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA (десктоп) */}
          <div className="hidden lg:block shrink-0">
            <Link href={AUTH_HREF}>
              <Button
                className={cn(
                  "text-white font-semibold border-0 rounded-xl cursor-pointer",
                  "hover:brightness-110 transition-all duration-200",
                  scrolled ? "h-8 px-4 text-xs" : "h-9 px-5 text-sm"
                )}
                style={{
                  background: "linear-gradient(135deg, #a855f7, #3b82f6)",
                }}
              >
                30 дней бесплатно
              </Button>
            </Link>
          </div>

          {/* Мобильное меню */}
          <button
            className="lg:hidden text-white/70 hover:text-white p-1.5 transition-colors duration-200"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label="Открыть меню"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </motion.div>
      </nav>

      {/* Мобильный оверлей */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 z-40 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              className="relative flex flex-col items-center justify-center h-full gap-8"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25, delay: 0.05 }}
            >
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-2xl text-white/60 hover:text-white transition-colors duration-200"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <Link href={AUTH_HREF} onClick={() => setMobileOpen(false)}>
                <Button
                  className="text-white font-semibold border-0 rounded-xl cursor-pointer h-12 px-8 text-base hover:brightness-110 transition-all duration-200"
                  style={{
                    background: "linear-gradient(135deg, #a855f7, #3b82f6)",
                  }}
                >
                  30 дней бесплатно
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
